import * as React from 'react';
import { ElementLoader } from 'ui';
import { AppSetting } from 'dependencies/appsettings';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { downloadFile } from 'app/infrastructure/platform/file';
import { STATE } from 'app/constants';
import { SpinLoader } from 'app/components';
import { actionCreators } from './actions';
import { BowtieInstanceState, BowtieStoreState } from './types-store';
import { BowtieExternalProps, BowtieCase, BowtieApp, BowtieTooltipType, DraftConnections, BowtieTooltipOptions } from './types';
import { BowtieOverlay } from 'app/area/bowtie/overlay/bowtie-overlay';
import { BowtieTooltip } from 'app/area/bowtie/tooltip/bowtie-tooltip';
import { mapDraftConnections } from './mapping/map-draft-connections';
import { bowtieTranslations } from './constants';
import { BowtieDisplayMode } from './external-types';
import { getDraftId } from './selectors';

import styles from './bowtie.scss';
import { BowtieFilters } from 'app/area/bowtie/filters/bowtie-filters';

type BowtieProps = DefaultContainerProps & BowtieExternalProps & typeof actionCreators & { instanceState: BowtieInstanceState };

interface BowtieComponentState {
  caseData: BowtieCase;
  draftConnections: DraftConnections[];
  tooltipOptions: BowtieTooltipOptions;
  left: number;
  right: number;
  top: number;
  type: BowtieTooltipType;
  showFilters: boolean;
}

export class BowtieComponent extends React.Component<BowtieProps, BowtieComponentState> {

  public static statePath: string = STATE.bowtie;

  private bowtie: BowtieApp;
  private bowtieElement: HTMLElement;

  public componentDidMount() {
    this.loadData();
  }

  public componentWillUnmount() {
    if (this.bowtie) {
      this.bowtie.dispose();
      this.bowtie = undefined;
    }
    this.bowtieElement = undefined;
    this.props.clean(this.props.instanceKey);
  }

  public componentDidUpdate(prevProps: BowtieProps) {
    const {
      caseDataModel, draftData, casesRequiringUpdate, requestId
    } = this.props?.instanceState || {};
    const { caseNumber } = this.props;
    if ((!!caseNumber && caseNumber !== prevProps.caseNumber) || 
      (prevProps.instanceState?.requestId !== undefined && requestId !== prevProps.instanceState?.requestId)) {
      this.loadData();

    } else if (caseDataModel !== prevProps.instanceState?.caseDataModel ||
      draftData !== prevProps.instanceState?.draftData ||
      casesRequiringUpdate !== prevProps.instanceState?.casesRequiringUpdate) {

      this.drawBowtie(false);
    }
  }

  private loadData = async () => {

    const { caseNumber, connectedDrafts, loadBowtie, instanceKey } = this.props;
    const { bowtieLayout } = this.props.instanceState || {};
    if (!caseNumber && caseNumber !== 0) {
      return;
    }

    const draftId = getDraftId(connectedDrafts);

    loadBowtie(bowtieLayout, caseNumber, draftId, instanceKey);
  };

  private connectCase = (caseNumber: number, connectionType: number) => {
    this.hideTooltip();
    this.props.addConnection(getDraftId(this.props.connectedDrafts), caseNumber, connectionType, this.props.instanceKey);
  };

  private disconnectCase = (caseNumber: number, connectionType: number) => {
    this.hideTooltip();
    this.props.removeConnection(getDraftId(this.props.connectedDrafts), caseNumber, connectionType, this.props.instanceKey);
  };

  private showTooltip = (caseData: BowtieCase, draftConnections: DraftConnections[],
    tooltipOptions: BowtieTooltipOptions,
    left: number, right: number, top: number) => {

    const { loadEvaluation } = this.props;
    const { inspectionEvaluations } = this.props.instanceState;

    const caseNumber = caseData.caseNumber;

    if (tooltipOptions.showEvaluations &&
      !inspectionEvaluations.evaluationStatuses[caseNumber] &&
      !inspectionEvaluations.fetching.includes(caseNumber)) {
      loadEvaluation(caseNumber, this.props.instanceKey);
    }

    this.setState({
      caseData, draftConnections, tooltipOptions, left, right, top, type: BowtieTooltipType.caseData
    });
  };

  private showMeatball = (caseData: BowtieCase, draftConnections: DraftConnections[], left: number, right: number, top: number) => {
    this.setState({
      caseData, draftConnections, left, right, top, type: BowtieTooltipType.connections
    });
  };

  private showFilters = () => {
    this.setState({
      showFilters: true
    });
  };

  private hideFilters = () => {
    this.setState({
      showFilters: false
    });
  };

  private hideTooltip = () => {
    this.setState({
      caseData: undefined
    });
  };

  private addBowtieCanvas = async (elem: HTMLElement) => {
    if (!elem) { return }
    const canvas = await import(/* webpackChunkName: "bowtie" */ 'app/components/bowtie/components/canvas');
    this.bowtie = new canvas.Canvas(elem, AppSetting.ApplicationUrl, {
      showTooltip: this.showTooltip,
      showMeatball: this.showMeatball,
      hideTooltip: this.hideTooltip
    },
      {
        downloadFile: downloadFile,
        openFilters: this.showFilters
      },
      styles);
    this.drawBowtie(true);
    this.bowtieElement = elem;
  };

  private drawBowtie = (resetZoom: boolean) => {

    const { fetchingBowtie, caseDataModel, bowtieLayout, caseTemplate, draftData, casesRequiringUpdate } = this.props.instanceState;

    if (!this.bowtie || fetchingBowtie || !caseDataModel) {
      return;
    }

    const connectMode = !!draftData;

    const dataModel = {
      caseDataModel: {
        ...caseDataModel,
        ...bowtieLayout
      },
      caseTemplate,
      draftConnections: mapDraftConnections(caseDataModel, draftData, casesRequiringUpdate)
    };

    this.bowtie.draw(dataModel, resetZoom, connectMode);
  };

  public render() {
    const { translation, locale, caseNumber, connectedDrafts, footer, displayMode, instanceKey } = this.props;
    const { caseDataModel, fetchingBowtie, inspectionEvaluations,
      connectionTypes, connectedCases,
      draftData, caseTemplate, stateForCaseIds } = this.props?.instanceState || {};

    const draftId = getDraftId(connectedDrafts);
    const loadConnectedCases = (caseId: number, type: number) => {
      this.props.loadConnectedCases(caseId, type, instanceKey);
    };

    return <BowtieOverlay
      displayOverlay={displayMode === BowtieDisplayMode.fullscreen}
      caseNumber={caseNumber}
      connectedDrafts={connectedDrafts}
      translation={translation} >
      {fetchingBowtie
        ? displayMode === BowtieDisplayMode.fullscreen ? <SpinLoader /> : <ElementLoader />
        : <div className={`${styles.bowtie} ${displayMode !== BowtieDisplayMode.fullscreen
          ? styles.compactMode
          : ''}`}
          ref={this.addBowtieCanvas}>
          {!!this?.state?.showFilters && <BowtieFilters onClose={this.hideFilters} locale={locale} instanceKey={instanceKey} />}
          {caseDataModel &&  <BowtieTooltip
            draftId={draftId}
            draftData={draftData}
            connectionTypes={connectionTypes}
            connectCase={this.connectCase}
            loadConnectedCases={loadConnectedCases}
            disconnectCase={this.disconnectCase}
            componentElement={this.bowtieElement}
            caseTemplate={caseTemplate}
            translation={translation}
            inspectionEvaluations={inspectionEvaluations}
            connectedCases={connectedCases}
            otherConnections={caseDataModel.otherConnections}
            stateForCaseIds={stateForCaseIds}
            {...this.state} />
          }
          {footer && <div className={styles.footer}>{footer}</div>}
        </div>}
    </BowtieOverlay>;
  }

}
const selectors = {
  instanceState: (state: BowtieStoreState, externalProps: BowtieExternalProps) => {
    return state.instances?.[externalProps.instanceKey] || {};
  }
};

const container = containerFactory<BowtieExternalProps>(BowtieComponent, actionCreators, selectors, bowtieTranslations);

export { container as Bowtie };
